"use client";

import { useState } from "react";
import { CircleHelp } from "lucide-react";

import { CHAIN_OPTIONS } from "../dashboard/dashboard-layout";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://forgeswap.fun";

const initialForm = {
  projectName: "",
  tokenAddress: "",
  chainId: String(CHAIN_OPTIONS[0].id),
  tokenSymbol: "",
  tokenLogoUrl: "",
  projectWebsite: "",
  contactEmail: "",
  contactTelegram: "",
  contactTwitter: "",
  message: "",
  promoCode: "",
};

export default function FeaturedPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [promoCodeValid, setPromoCodeValid] = useState<boolean | null>(null);
  const [promoCodeMarketer, setPromoCodeMarketer] = useState<string | null>(null);
  const [checkingPromoCode, setCheckingPromoCode] = useState(false);

  const update = (key: keyof typeof initialForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    // Reset promo code validation when code changes
    if (key === "promoCode") {
      setPromoCodeValid(null);
      setPromoCodeMarketer(null);
    }
  };

  const validatePromoCode = async () => {
    const code = form.promoCode.trim();
    if (!code) {
      setPromoCodeValid(null);
      setPromoCodeMarketer(null);
      return;
    }

    setCheckingPromoCode(true);
    try {
      const res = await fetch("/api/promo-codes/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      setPromoCodeValid(data.valid);
      setPromoCodeMarketer(data.valid ? data.marketerName : null);
    } catch {
      setPromoCodeValid(false);
      setPromoCodeMarketer(null);
    } finally {
      setCheckingPromoCode(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.projectName.trim() || !form.tokenAddress.trim() || !form.tokenSymbol.trim() || !form.chainId) {
      setError("Please fill in Project Name, Token Address, Chain, and Token Symbol.");
      return;
    }

    if (!form.contactEmail.trim() && !form.contactTelegram.trim() && !form.contactTwitter.trim()) {
      setError("Please provide at least one contact method — Email, Telegram, or Twitter.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/featured/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          projectName: form.projectName.trim(),
          tokenAddress: form.tokenAddress.trim(),
          tokenSymbol: form.tokenSymbol.trim(),
          tokenLogoUrl: form.tokenLogoUrl.trim(),
          projectWebsite: form.projectWebsite.trim(),
          contactEmail: form.contactEmail.trim(),
          contactTelegram: form.contactTelegram.trim(),
          contactTwitter: form.contactTwitter.trim(),
          message: form.message.trim(),
          promoCode: form.promoCode.trim().toLowerCase() || undefined,
          chainId: form.chainId,
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || "Something went wrong.");
        return;
      }

      setSubmitted(true);
      setForm(initialForm);
    } catch (e) {
      console.error(e);
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ background: "#111", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif", padding: 24 }}>
        <div style={{ maxWidth: 560, textAlign: "center", padding: 40, background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>⭐</div>
          <h1 style={{ color: "#f9cd4f", fontSize: 32, fontWeight: 700, marginBottom: 16 }}>Thanks for your submission!</h1>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7, marginBottom: 8 }}>We received your featured token request and will review it shortly.</p>
          <p style={{ color: "#aaa", fontSize: 16, lineHeight: 1.7 }}>If you provided an email address, we’ll follow up with next steps and confirmation.</p>
          <a href={BASE_URL} style={{ display: "inline-block", marginTop: 32, background: "#f9cd4f", color: "#111", padding: "12px 32px", borderRadius: 10, fontWeight: 700, textDecoration: "none" }}>Back to ForgeSwap</a>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div style={{ background: "#111", minHeight: "100vh", color: "#fff", fontFamily: "Inter, sans-serif", overflow: "hidden", maxWidth: "100vw" }}>
        <div style={{ background: "linear-gradient(135deg, #1a1a1a, #111)", borderBottom: "1px solid #222", padding: "48px 16px 32px" }}>
          <div style={{ maxWidth: 840, margin: "0 auto", textAlign: "center" }}>
            <h1 style={{ color: "#f9cd4f", fontSize: "clamp(28px, 6vw, 42px)", fontWeight: 800, marginBottom: 16 }}>Get Your Token Featured</h1>
            <p style={{ color: "#ccc", fontSize: 18, lineHeight: 1.7, maxWidth: 680, margin: "0 auto" }}>Apply to place your token in ForgeSwap’s featured list and get more visibility from active traders.</p>
          </div>
        </div>

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 0", display: "grid" }}>
          <div style={{ background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
            <div style={{ display: "grid", gap: 24 }}>
              <div>
                <h2 style={{ color: "#f9cd4f", fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 800, margin: "0 0 12px" }}>Featured Token Listing</h2>
                <p style={{ color: "#fff", fontSize: 16, lineHeight: 1.8, margin: 0, maxWidth: 880 }}>
                  Get your token in front of traders across 30+ chains. Featured tokens appear in the ForgeSwap token dropdown, giving your community instant access to buy with Apple Pay, Google Pay, or credit/debit card — no crypto experience required.
                </p>
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, margin: 0 }}>What's included</h3>
                <div style={{ display: "grid", gap: 10 }}>
                  {[
                    "Featured placement in the ForgeSwap token dropdown",
                    "Direct purchase via Apple Pay, Google Pay & credit/debit card",
                    "10% of all swap fees generated by your token (from ForgeSwap's 1.25% fee)",
                    "Monthly marketing exposure across our whitelabel swap network",
                    "Revenue paid out 2x weekly",
                  ].map((item) => (
                    <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "rgba(17,17,17,0.9)", border: "1px solid rgba(249,205,79,0.14)", borderRadius: 14, padding: "14px 16px" }}>
                      <span style={{ color: "#f9cd4f", fontSize: 18, lineHeight: 1, fontWeight: 700 }}>✓</span>
                      <span style={{ color: "#fff", fontSize: 15, lineHeight: 1.7 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ justifySelf: "start", background: "rgba(249,205,79,0.12)", border: "1px solid rgba(249,205,79,0.4)", color: "#f9cd4f", borderRadius: 999, padding: "14px 20px", fontSize: 18, fontWeight: 800, letterSpacing: 0.2 }}>
                $500 setup fee · $50/month
              </div>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px 48px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 32 }}>
          <div style={{ background: "linear-gradient(180deg,rgba(22,22,26,0.95)_0%,rgba(16,16,20,0.95)_100%)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "1px solid rgba(255,255,255,0.13)", borderRadius: 20, padding: 28, boxShadow: "0 8px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)" }}>
            <h2 style={{ color: "#f9cd4f", fontSize: 22, fontWeight: 700, margin: "0 0 8px" }}>Featured token request</h2>
            <p style={{ color: "#888", fontSize: 14, margin: "0 0 28px" }}>Share your token details and preferred contact method. We review every featured request manually.</p>

            {error ? (
              <div style={{ background: "#ef444422", border: "1px solid #ef4444", color: "#ef4444", padding: "12px 16px", borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
                {error}
              </div>
            ) : null}

            <div style={{ display: "grid", gap: 16 }}>
              {[
                { key: "projectName", label: "Project Name *", placeholder: "e.g. Forge Alpha" },
                { key: "tokenAddress", label: "Token Address *", placeholder: "0x... or Solana address" },
                { key: "tokenSymbol", label: "Token Symbol *", placeholder: "e.g. FORGE" },
                { key: "projectWebsite", label: "Project Website", placeholder: "https://example.com" },
                { key: "contactEmail", label: "Contact Email", placeholder: "team@example.com" },
                { key: "contactTelegram", label: "Contact Telegram", placeholder: "@projectteam" },
                { key: "contactTwitter", label: "Contact Twitter", placeholder: "@projectteam" },
              ].map((field) => (
                <div key={field.key}>
                  <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>{field.label}</label>
                  <input
                    value={form[field.key as keyof typeof initialForm]}
                    onChange={(e) => update(field.key as keyof typeof initialForm, e.target.value)}
                    placeholder={field.placeholder}
                    style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }}
                  />
                </div>
              ))}

              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Chain *</label>
                <select value={form.chainId} onChange={(e) => update("chainId", e.target.value)} style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14 }}>
                  {CHAIN_OPTIONS.map((chain) => (
                    <option key={chain.id} value={chain.id}>{chain.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#888", fontSize: 12, marginBottom: 6 }}>
                  Token Logo URL
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" style={{ background: "transparent", border: "none", color: "#f9cd4f", display: "inline-flex", alignItems: "center", padding: 0, cursor: "help" }}>
                        <CircleHelp size={14} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-[#111] text-white border-[#333]">
                      Tip: upload your image to Imgur, then copy the direct image URL into this field.
                    </TooltipContent>
                  </Tooltip>
                </label>
                <input value={form.tokenLogoUrl} onChange={(e) => update("tokenLogoUrl", e.target.value)} placeholder="https://..." style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box" }} />
                <a href="https://imgur.com/upload" target="_blank" rel="noreferrer" style={{ color: "#f9cd4f", fontSize: 12, textDecoration: "none", display: "inline-block", marginTop: 8 }}>Upload to Imgur to get a direct URL →</a>
              </div>

              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Promo Code</label>
                <input
                  value={form.promoCode}
                  onChange={(e) => update("promoCode", e.target.value)}
                  onBlur={validatePromoCode}
                  placeholder="Optional promo code"
                  style={{
                    width: "100%",
                    background: "#111",
                    border: `1px solid ${promoCodeValid === true ? "#22c55e" : promoCodeValid === false ? "#ef4444" : "#333"}`,
                    color: "#fff",
                    padding: "12px 14px",
                    borderRadius: 10,
                    fontSize: 14,
                    boxSizing: "border-box",
                    textTransform: "uppercase",
                  }}
                />
                {checkingPromoCode && (
                  <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>Checking code...</div>
                )}
                {promoCodeValid === true && promoCodeMarketer && (
                  <div style={{ color: "#22c55e", fontSize: 12, marginTop: 6 }}>✓ Code applied ({promoCodeMarketer})</div>
                )}
                {promoCodeValid === false && form.promoCode.trim() && (
                  <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>Invalid or inactive promo code</div>
                )}
              </div>

              <div>
                <label style={{ display: "block", color: "#888", fontSize: 12, marginBottom: 6 }}>Message</label>
                <textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your project, timing, or any special requests." rows={5} style={{ width: "100%", background: "#111", border: "1px solid #333", color: "#fff", padding: "12px 14px", borderRadius: 10, fontSize: 14, boxSizing: "border-box", resize: "vertical" }} />
              </div>

              <button onClick={handleSubmit} disabled={submitting} style={{ background: "#f9cd4f", color: "#111", border: "none", padding: "12px 18px", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                {submitting ? "Submitting..." : "Submit Featured Request"}
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 20 }}>
            <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
              <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>What to include</h3>
              <div style={{ display: "grid", gap: 12 }}>
                {[
                  "A valid token address and the chain where your token trades.",
                  "Your token symbol and an optional logo URL for review.",
                  "At least one contact method so we can coordinate quickly.",
                  "Any helpful notes about your launch, campaign, or timeline.",
                ].map((item) => (
                  <div key={item} style={{ color: "#ccc", fontSize: 14, lineHeight: 1.7, background: "#111", border: "1px solid #222", borderRadius: 12, padding: 14 }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "#1a1a1a", border: "1px solid #222", borderRadius: 16, padding: 24 }}>
              <h3 style={{ color: "#f9cd4f", fontSize: 18, fontWeight: 700, margin: "0 0 12px" }}>Why get featured?</h3>
              <div style={{ display: "grid", gap: 12 }}>
                {[
                  "More visibility from active ForgeSwap traders.",
                  "A cleaner path for your community to discover your token.",
                  "Manual review keeps the featured list curated and trusted.",
                ].map((item) => (
                  <div key={item} style={{ color: "#ccc", fontSize: 14, lineHeight: 1.7, background: "#111", border: "1px solid #222", borderRadius: 12, padding: 14 }}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
